import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useI18n } from "./i18n/I18nProvider";
import {
  DEFAULT_SITE_URL,
  PERSON_NAME,
  PERSON_SAME_AS,
} from "./seoConfig";

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function resolvePageMeta(pathname) {
  const path = normalizePath(pathname);
  if (path === "/") return { pageKey: "home", noindex: false };
  if (path.startsWith("/portfolioDetails/"))
    return { pageKey: "portfolioDetailShell", noindex: false };
  if (path === "/viewKanLiyaAna") return { pageKey: "stats", noindex: true };
  const seg = path.slice(1);
  const known = ["about", "portfolio", "blog", "contact"];
  if (known.includes(seg)) return { pageKey: seg, noindex: false };
  return { pageKey: "home", noindex: false };
}

const MetaTags = () => {
  const { t, locale, isRtl } = useI18n();
  const { pathname } = useLocation();
  const { pageKey, noindex } = resolvePageMeta(pathname);

  const title = t(`meta.pages.${pageKey}.title`);
  const description = t(`meta.pages.${pageKey}.description`);

  const origin =
    typeof window !== "undefined" ? window.location.origin : DEFAULT_SITE_URL;
  const canonicalPath = pathname || "/";
  const canonical = `${origin}${canonicalPath === "" ? "/" : canonicalPath}`;
  const ogImage = `${origin}/images/logo.png`;
  const ogUrl = canonical;

  const jsonLd = useMemo(() => {
    const graph = [
      {
        "@type": "Person",
        "@id": `${origin}/#person`,
        name: PERSON_NAME,
        url: origin + "/",
        jobTitle: t("meta.schema.jobTitle"),
        sameAs: PERSON_SAME_AS,
      },
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        name: PERSON_NAME,
        url: origin + "/",
        publisher: { "@id": `${origin}/#person` },
        inLanguage: ["en", "fr", "ar"],
      },
    ];
    return {
      "@context": "https://schema.org",
      "@graph": graph,
    };
  }, [origin, t]);

  return (
    <Helmet
      htmlAttributes={{
        lang: locale,
        dir: isRtl ? "rtl" : "ltr",
      }}
    >
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={t("meta.keywords")} />
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={t("meta.ogLocale")} />
      <meta property="og:site_name" content={PERSON_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default MetaTags;
